import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function Callback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The Supabase client's `detectSessionInUrl: true` option parses the
    // OAuth code/tokens out of the URL and establishes the session
    // automatically on load — we just need to wait for it to land.
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const finish = async () => {
      const { data, error: err } = await supabase.auth.getSession();
      if (cancelled) return;
      if (err) { setError(err.message); return; }
      if (data.session) { navigate('/dashboard', { replace: true }); return; }

      // Session not established yet on this tick — listen for it.
      const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
        if (cancelled) return;
        if (session) navigate('/dashboard', { replace: true });
      });
      unsubscribe = () => listener.subscription.unsubscribe();

      timeoutId = setTimeout(() => {
        if (!cancelled) setError(prev => prev ?? 'Sign-in timed out. Please try again.');
      }, 8000);
    };

    void finish();

    // This is the cleanup React actually registers (unlike the old version,
    // which returned the unsubscribe function from the inner async `finish`
    // — a value nothing ever reads, since `finish()` is called fire-and-forget).
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate]);

  if (error) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 24,
        background: 'var(--bg-base)', padding: '0 24px', textAlign: 'center',
      }}>
        <div style={{ fontSize: 48, opacity: 0.4 }}>⚠</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--text-primary)', fontWeight: 700 }}>
          Sign-in failed
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, maxWidth: 480, lineHeight: 1.7 }}>
          {error}
        </p>
        <button
          onClick={() => navigate('/login', { replace: true })}
          style={{
            padding: '10px 24px', background: 'var(--accent-primary)', color: '#000',
            border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
          }}
        >
          ← Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 16,
      background: 'var(--bg-base)',
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        border: '2px solid rgba(138,92,246,0.15)',
        borderTopColor: 'var(--accent-primary)',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ fontSize: 16, color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
        Signing you in…
      </p>
    </div>
  );
}
